// src/components/legalContent.js
// =============================================================================
// JSX content for the Privacy Policy and Terms & Conditions modals shown in
// the client portal footer. Kept separate from VersionFooter.js so the footer
// stays lean.
// =============================================================================

/**
 * @fileoverview Legal content (Privacy Policy & Terms and Conditions) rendered
 * inside the InfoModal triggered from VersionFooter.
 * @module components/legalContent
 */

import React from "react";

/** Effective date displayed at the top of both legal documents. */
export const LEGAL_EFFECTIVE_DATE = "May 7, 2026";

/**
 * Privacy Policy JSX content for the Red Setter Homes Client Portal.
 * @type {React.ReactElement}
 */
export const PRIVACY_POLICY_CONTENT = (
  <div className="legal-content">
    <p className="legal-effective-date">
      <em>Effective Date: {LEGAL_EFFECTIVE_DATE}</em>
    </p>
    <p>
      Red Setter Homes LLC ("Red Setter Homes," "we," "us," or "our") is
      committed to protecting the privacy of our clients and portal users. This
      Privacy Policy describes how we collect, use, disclose, and safeguard your
      information when you access the Red Setter Homes Client Portal at
      my.redsetterhome.com (the "Portal"). Please read this policy carefully. By
      accessing or using the Portal, you agree to the terms of this Privacy
      Policy.
    </p>

    <h2>1. Information We Collect</h2>

    <h3>1.1 Information You Provide</h3>
    <p>
      When you use the Portal, we may collect information that you voluntarily
      provide, including:
    </p>
    <ul>
      <li>Your name, email address, phone number, and mailing address</li>
      <li>
        Project-related information such as construction selections, material
        preferences, and design choices
      </li>
      <li>Change order requests and approvals</li>
      <li>Documents, photos, and files you upload to the Portal</li>
      <li>Communications and messages submitted through the Portal</li>
      <li>Profile information you provide when managing your account</li>
    </ul>

    <h3>1.2 Information Collected Automatically</h3>
    <p>
      When you access the Portal, certain information may be collected
      automatically, including:
    </p>
    <ul>
      <li>Device information (browser type, operating system, device type)</li>
      <li>Log data (IP address, access times, pages viewed, referring URLs)</li>
      <li>Usage data (features accessed, actions taken within the Portal)</li>
      <li>
        Cookies and similar tracking technologies used to maintain your session
        and preferences
      </li>
    </ul>

    <h3>1.3 Information from Third-Party Services</h3>
    <p>
      If you choose to sign in using Google Sign-In or a Microsoft account, we
      receive your name, email address, and profile photo from the respective
      provider. We do not receive your Google or Microsoft password. We use
      Firebase Authentication (provided by Google) to manage sign-in, and
      Cloudinary and Airtable to store uploaded media files. These services may
      collect limited technical data as described in their respective privacy
      policies.
    </p>

    <h2>2. How We Use Your Information</h2>
    <p>We use the information we collect for the following purposes:</p>
    <ul>
      <li>To provide, operate, and maintain the Portal and its features</li>
      <li>
        To manage your construction project, including tracking progress,
        selections, tasks, and change orders
      </li>
      {/* Future feature 
      <li>
        To communicate with you about your project, including sending status
        updates and notifications
      </li> 
      */}
      <li>To authenticate your identity and manage account access</li>
      <li>
        To store and organize project-related documents, photos, and files
      </li>
      <li>
        To monitor and improve the Portal's performance, security, and user
        experience
      </li>
      <li>
        To diagnose technical issues and errors using our error-tracking service
      </li>
      <li>
        To comply with legal obligations and enforce our Terms and Conditions
      </li>
    </ul>

    <h2>3. How We Share Your Information</h2>
    <p>
      We do not sell your personal information. We may share your information in
      the following limited circumstances:
    </p>
    <ul>
      <li>
        <strong>Service Providers:</strong> We use third-party service providers
        to operate the Portal, including Firebase (authentication and hosting),
        Airtable (data management), Cloudinary (media storage), Sentry (error
        tracking), and Google Analytics (usage analytics). These providers only
        access your data as necessary to perform their functions and are
        obligated to protect it.
      </li>
      <li>
        <strong>Red Setter Homes Team Members:</strong> Authorized employees and
        contractors of Red Setter Homes may access your project information
        through the administrative portal to manage your construction project.
      </li>
      <li>
        <strong>Vendors and Subcontractors:</strong> We may share limited
        project-related information with vendors and subcontractors assigned to
        your project as necessary to complete construction tasks.
      </li>
      <li>
        <strong>Legal Requirements:</strong> We may disclose your information if
        required by law, in response to a court order or subpoena, or to protect
        the rights, property, or safety of Red Setter Homes, our clients, or
        others.
      </li>
    </ul>

    <h2>4. Data Storage and Security</h2>
    <p>
      Your data is stored using industry-standard cloud services, including
      Google Firebase and Airtable, which employ encryption in transit and at
      rest. Uploaded media files are stored via Cloudinary. We implement
      reasonable administrative, technical, and physical security measures to
      protect your information from unauthorized access, alteration, disclosure,
      or destruction. However, no method of electronic transmission or storage
      is completely secure, and we cannot guarantee absolute security.
    </p>

    <h2>5. Data Retention</h2>
    <p>
      We retain your personal information and project data for as long as your
      construction project is active and for a reasonable period afterward to
      fulfill warranty obligations, comply with legal requirements, resolve
      disputes, and enforce our agreements. If you wish to request deletion of
      your data, please contact us using the information provided below.
    </p>

    <h2>6. Your Rights and Choices</h2>
    <p>
      Depending on your jurisdiction, you may have certain rights regarding your
      personal information, including:
    </p>
    <ul>
      <li>The right to access the personal information we hold about you</li>
      <li>The right to request correction of inaccurate information</li>
      <li>
        The right to request deletion of your personal information, subject to
        certain exceptions
      </li>
      <li>
        The right to object to or restrict certain processing of your data
      </li>
    </ul>
    <p>
      To exercise any of these rights, please contact us at the information
      provided in Section 10 below. We will respond to your request within a
      reasonable timeframe.
    </p>

    <h2>7. Cookies and Tracking Technologies</h2>
    <p>
      The Portal uses cookies and similar technologies to maintain your
      authentication session, remember your preferences, and analyze usage
      patterns through Google Analytics. You can manage cookie preferences
      through your browser settings. Please note that disabling cookies may
      affect the Portal's functionality, including the ability to remain signed
      in.
    </p>

    <h2>8. Children's Privacy</h2>
    <p>
      The Portal is not intended for use by individuals under the age of 18. We
      do not knowingly collect personal information from children. If you
      believe a child has provided us with personal information, please contact
      us so that we can take appropriate action.
    </p>

    <h2>9. Changes to This Privacy Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. When we make material
      changes, we will update the "Effective Date" at the top of this document
      and may notify you through the Portal. Your continued use of the Portal
      after any changes constitutes your acceptance of the updated policy.
    </p>

    <h2>10. Contact Us</h2>
    <p>
      If you have questions or concerns about this Privacy Policy or our data
      practices, please contact us at:
    </p>
    <p className="legal-contact">
      <strong>Red Setter Homes LLC</strong>
      <br />
      Mail: 5900 Spout Springs Rd, Suite 3C, PMB14, Flowery Branch, GA 30542
      <br />
      Phone: (678) 247-3735
      <br />
      Website: redsetterhome.com
      <br />
      Email: info@redsetterhome.com
    </p>
  </div>
);

/**
 * Terms and Conditions JSX content for the Red Setter Homes Client Portal.
 * @type {React.ReactElement}
 */
export const TERMS_CONTENT = (
  <div className="legal-content">
    <p className="legal-effective-date">
      <em>Effective Date: {LEGAL_EFFECTIVE_DATE}</em>
    </p>
    <p>
      These Terms and Conditions ("Terms") govern your access to and use of the
      Red Setter Homes Client Portal located at my.redsetterhome.com (the
      "Portal"), operated by Red Setter Homes LLC ("Red Setter Homes," "we,"
      "us," or "our"). By accessing or using the Portal, you agree to be bound
      by these Terms. If you do not agree to these Terms, you must not access or
      use the Portal.
    </p>

    <h2>1. Eligibility and Account Access</h2>
    <p>
      The Portal is provided exclusively to clients of Red Setter Homes who have
      an active construction project or are within a warranty period. Access to
      the Portal is granted by Red Setter Homes and is not available for public
      registration. You may sign in using Google Sign-In or a magic link sent to
      the email address associated with your account. You are responsible for
      maintaining the confidentiality of your account credentials and for all
      activity that occurs under your account. You agree to notify us
      immediately of any unauthorized use of your account.
    </p>

    <h2>2. Purpose of the Portal</h2>
    <p>
      The Portal is a project management tool designed to facilitate
      communication and information sharing between Red Setter Homes and our
      clients during the construction process. The Portal allows you to:
    </p>
    <ul>
      <li>
        View your project's current status, construction phase, and progress
      </li>
      <li>
        Review and submit home design selections (materials, fixtures, finishes,
        and other choices)
      </li>
      <li>View and manage change orders related to your project</li>
      <li>Access project documents, photos, plans, and uploaded files</li>
      <li>Track construction tasks and milestones</li>
      <li>Manage paint and stain selections</li>
      <li>Track owner-purchased items and delivery schedules</li>
    </ul>

    <h2>3. Acceptable Use</h2>
    <p>
      You agree to use the Portal only for its intended purpose and in
      compliance with all applicable laws. You agree not to:
    </p>
    <ul>
      <li>
        Share your login credentials or allow unauthorized individuals to access
        the Portal using your account
      </li>
      <li>Attempt to access data or projects belonging to other clients</li>
      <li>
        Upload malicious files, viruses, or any content that could harm the
        Portal or its users
      </li>
      <li>
        Attempt to reverse-engineer, hack, or disrupt the Portal's functionality
        or infrastructure
      </li>
      <li>
        Use the Portal for any purpose other than managing your Red Setter Homes
        construction project
      </li>
      <li>
        Reproduce, distribute, or publicly display any content from the Portal
        without prior written consent
      </li>
      <li>
        Use automated scripts, bots, or scrapers to access or extract data from
        the Portal
      </li>
    </ul>

    <h2>4. User Content</h2>
    <p>
      You may upload documents, photos, and other files to the Portal in
      connection with your construction project ("User Content"). You retain
      ownership of your User Content. By uploading User Content to the Portal,
      you grant Red Setter Homes a non-exclusive, royalty-free license to use,
      store, display, and share your User Content as necessary to provide the
      Portal's services and manage your construction project. You represent and
      warrant that you have the right to upload any User Content you submit and
      that such content does not infringe upon the rights of any third party.
    </p>

    <h2>5. Project Information and Selections</h2>
    <p>
      The Portal displays project information, construction progress, and other
      data as entered by the Red Setter Homes team. While we strive for
      accuracy, the information displayed on the Portal is for general reference
      purposes and may not reflect real-time conditions at your job site.
      Construction schedules, completion dates, and progress percentages are
      estimates and are subject to change due to weather, material availability,
      permitting, or other factors beyond our control. Selections and change
      orders submitted through the Portal are subject to review and confirmation
      by the Red Setter Homes team. Submission through the Portal does not
      constitute final approval unless expressly confirmed by Red Setter Homes.
    </p>

    <h2>6. Intellectual Property</h2>
    <p>
      The Portal, including its design, source code, features, functionality,
      graphics, and branding, is the property of Red Setter Homes LLC and is
      protected by applicable intellectual property laws. You may not copy,
      modify, distribute, or create derivative works based on the Portal or any
      of its content without our prior written consent. The Red Setter Homes
      name, logo, and all related marks are trademarks of Red Setter Homes LLC.
    </p>

    <h2>7. Third-Party Services</h2>
    <p>
      The Portal relies on third-party services to operate, including Google
      Firebase for authentication and hosting, Airtable for data management,
      Cloudinary for media file storage, Sentry for error monitoring, and Google
      Analytics for usage analytics. Your use of the Portal may be subject to
      the terms and conditions and privacy policies of these third-party
      providers. Red Setter Homes is not responsible for the practices or
      policies of third-party service providers.
    </p>

    <h2>8. Availability and Modifications</h2>
    <p>
      While we strive to keep the Portal available and functional at all times,
      we do not guarantee uninterrupted or error-free access. The Portal may be
      temporarily unavailable due to maintenance, updates, or technical issues.
      We reserve the right to modify, suspend, or discontinue any feature or
      functionality of the Portal at any time, with or without notice. We are
      not liable for any loss or inconvenience caused by the Portal's
      unavailability.
    </p>

    <h2>9. Limitation of Liability</h2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, RED SETTER HOMES LLC,
      ITS OWNERS, EMPLOYEES, AND CONTRACTORS SHALL NOT BE LIABLE FOR ANY
      INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING
      FROM OR RELATED TO YOUR USE OF THE PORTAL, INCLUDING BUT NOT LIMITED TO
      LOSS OF DATA, LOSS OF PROFITS, OR BUSINESS INTERRUPTION, REGARDLESS OF THE
      THEORY OF LIABILITY. THE PORTAL IS PROVIDED "AS IS" AND "AS AVAILABLE"
      WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
      NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
      PARTICULAR PURPOSE, AND NON-INFRINGEMENT. OUR TOTAL LIABILITY FOR ANY
      CLAIM ARISING FROM YOUR USE OF THE PORTAL SHALL NOT EXCEED ONE HUNDRED
      DOLLARS ($100).
    </p>

    <h2>10. Indemnification</h2>
    <p>
      You agree to indemnify, defend, and hold harmless Red Setter Homes LLC,
      its owners, employees, and contractors from and against any claims,
      liabilities, damages, losses, and expenses (including reasonable
      attorneys' fees) arising from or related to your use of the Portal, your
      violation of these Terms, or your infringement of any third-party rights.
    </p>

    <h2>11. Termination</h2>
    <p>
      We may suspend or terminate your access to the Portal at any time, with or
      without cause, and with or without notice. Upon termination of your
      construction project or warranty period, your access to the Portal may be
      revoked. You may request termination of your account by contacting us
      directly. Upon termination, your right to use the Portal ceases
      immediately, though we may retain your project data as described in our
      Privacy Policy.
    </p>

    <h2>12. Governing Law and Dispute Resolution</h2>
    <p>
      These Terms shall be governed by and construed in accordance with the laws
      of the State of Georgia, without regard to its conflict of law provisions.
      Any disputes arising from or relating to these Terms or your use of the
      Portal shall be resolved exclusively in the state or federal courts
      located in Hall County, Georgia. You consent to the personal jurisdiction
      of such courts.
    </p>

    <h2>13. Severability</h2>
    <p>
      If any provision of these Terms is found to be invalid or unenforceable,
      the remaining provisions shall continue in full force and effect. The
      invalid or unenforceable provision shall be modified to the minimum extent
      necessary to make it valid and enforceable.
    </p>

    <h2>14. Entire Agreement</h2>
    <p>
      These Terms, together with the Privacy Policy, constitute the entire
      agreement between you and Red Setter Homes LLC regarding your use of the
      Portal. These Terms do not modify, replace, or supersede any separate
      construction contract, builder agreement, or other written agreement
      between you and Red Setter Homes.
    </p>

    <h2>15. Changes to These Terms</h2>
    <p>
      We reserve the right to update or modify these Terms at any time. When we
      make material changes, we will update the "Effective Date" at the top of
      this document and may notify you through the Portal. Your continued use of
      the Portal after any changes constitutes your acceptance of the revised
      Terms.
    </p>

    <h2>16. Contact Us</h2>
    <p>If you have questions about these Terms, please contact us at:</p>
    <p className="legal-contact">
      <strong>Red Setter Homes LLC</strong>
      <br />
      Mail: 5900 Spout Springs Rd, Suite 3C, PMB14, Flowery Branch, GA 30542
      <br />
      Phone: (678) 247-3735
      <br />
      Website: redsetterhome.com
      <br />
      Email: info@redsetterhome.com
    </p>
  </div>
);
